/**
 * Dev-only mock Genesys bridge.
 *
 * Replaces the Genesys notification WebSocket + Conversations API with an
 * in-memory conversation driven by `postMessage` from a parent harness page
 * (see `dev/genesys-harness.html`). It deliberately reuses the REAL detection
 * code in `GenesysWebSocketService` (`handleMessage`, `handleMessageForBackend`,
 * `_detectSegmentBoundary`, `_extractDealerNumber`) so the mock exercises the
 * exact same logic the production embed does — only the Genesys transport is
 * faked. The backend/middleware WebSocket stays real.
 *
 * Guarded entirely behind `isMockMode()`; never bundled into production.
 */
import { useAuthStore } from '@/stores/auth';
import { useMessageStore } from '@/stores/messages';
import { MESSAGE_DIRECTION, PARTICIPANT_TYPE } from '@/constants';
import { GenesysWebSocketService } from '@/services/GenesysWebSocketService';
import { BackendWebSocketService } from '@/services/BackendWebSocketService';
import { getHarnessOrigin, postToHarness } from './mockMode';

interface MockParticipant {
  id: string;
  purpose: string;
  endTime?: string;
  attributes?: Record<string, string>;
}

interface TranscriptEntry {
  id: string;
  text: string;
  sender: 'customer' | 'agent';
}

type Phase = 'active' | 'ended' | 'resumed';

interface InitPayload {
  dealerNumber?: string;
  transcript?: TranscriptEntry[];
  phase?: Phase;
  boundaryTime?: string;
}

/** Commands accepted from the harness (all prefixed `mock:`). */
interface MockCommand {
  type: string;
  text?: string;
  id?: string;
  time?: string;
  dealerNumber?: string;
  payload?: InitPayload;
}

const DEALER_ATTRIBUTE_KEY = 'dealerNumber';

export const MockGenesysBridge = {
  started: false,
  participants: [] as MockParticipant[],
  boundaryCounter: 0,
  lastAiResponseCount: 0,

  /** Wire up the bridge: listen for harness commands and announce readiness. */
  start(): void {
    if (this.started) return;
    this.started = true;

    const messageStore = useMessageStore();
    // The Genesys transport is "connected" the moment the mock is live.
    messageStore.setConnectionStatus(true);

    // Relay Pete's observable state back to the harness on every store change.
    messageStore.$subscribe(() => this._postState());

    window.addEventListener('message', this._onMessage);

    this._reset();
    this._post({ type: 'pete:ready' });
  },

  /** Tear down listeners (used on unmount). */
  stop(): void {
    if (!this.started) return;
    window.removeEventListener('message', this._onMessage);
    this.started = false;
  },

  // ---- harness -> FE ------------------------------------------------------

  _onMessage(event: MessageEvent): void {
    // Only accept messages from the embedding harness origin.
    if (event.origin !== getHarnessOrigin()) return;

    const data = event.data as MockCommand | undefined;
    if (!data || typeof data.type !== 'string' || !data.type.startsWith('mock:'))
      return;

    // `this` is unbound in the listener; delegate to the singleton.
    MockGenesysBridge._handle(data);
  },

  _handle(command: MockCommand): void {
    switch (command.type) {
      case 'mock:init':
        this._init(command.payload ?? {});
        break;
      case 'mock:customer-message':
        this._addLiveMessage(command.text ?? '', 'customer', command.id);
        break;
      case 'mock:agent-message':
        this._addLiveMessage(command.text ?? '', 'agent', command.id);
        break;
      case 'mock:end':
        this._end();
        break;
      case 'mock:resume':
        this._resume(command.time);
        break;
      case 'mock:set-dealer':
        this._setDealer(command.dealerNumber ?? '');
        break;
      case 'mock:reset':
        this._reset();
        break;
      default:
        // Unknown command; ignore.
        break;
    }
  },

  /**
   * Rebuild the whole scenario from the harness's authoritative state. Used on
   * first load AND after an iframe reload, so the transcript is repopulated for
   * DISPLAY only (no re-forwarding), exactly mirroring `loadConversationHistory`
   * — which is what makes the rehydrate/no-re-prompt behaviour testable.
   */
  _init(payload: InitPayload): void {
    const authStore = useAuthStore();
    const messageStore = useMessageStore();

    messageStore.clearMessages();

    this.participants = this._buildParticipants(
      payload.phase ?? 'active',
      payload.boundaryTime,
      payload.dealerNumber
    );

    // Dealer resolution mirrors loadConversationHistory().
    const dealer = GenesysWebSocketService._extractDealerNumber(
      this.participants as never
    );
    authStore.setDealerNumber(dealer);
    if (dealer && BackendWebSocketService.isConnected) {
      void BackendWebSocketService.sendDealerInfo(dealer);
    }

    // Repopulate transcript for display only (no worker forward on reload).
    for (const entry of payload.transcript ?? []) {
      messageStore.addMessage(this._toMessage(entry));
    }

    // Run the real participant-detection path (end marker + boundary + resume).
    this._applyParticipants(this.participants);
  },

  _addLiveMessage(
    text: string,
    sender: 'customer' | 'agent',
    id?: string
  ): void {
    if (!text.trim()) return;
    const messageStore = useMessageStore();
    const message = this._toMessage({
      id: id || `${sender}-${crypto.randomUUID()}`,
      text,
      sender,
    });

    messageStore.addMessage(message);
    // Forward to the real middleware exactly like fetchMessageDetails does.
    void GenesysWebSocketService.handleMessageForBackend(message);
  },

  _end(): void {
    const now = new Date().toISOString();
    this.participants = this.participants.map((participant) => ({
      ...participant,
      endTime: participant.endTime || now,
    }));
    this._applyParticipants(this.participants);
  },

  _resume(boundaryTime?: string): void {
    this.boundaryCounter += 1;
    const now = boundaryTime || new Date().toISOString();

    // The prior customer segment closes; the agent (and a new customer) become
    // active again -> a detectable boundary.
    const next: MockParticipant[] = this.participants.map((participant) => {
      if (participant.purpose === PARTICIPANT_TYPE.CUSTOMER && !participant.endTime) {
        return { ...participant, endTime: now };
      }
      if (participant.purpose === PARTICIPANT_TYPE.AGENT) {
        return { ...participant, endTime: undefined };
      }
      return participant;
    });
    next.push({
      id: `customer-resume-${this.boundaryCounter}`,
      purpose: PARTICIPANT_TYPE.CUSTOMER,
    });

    this.participants = next;
    this._applyParticipants(next);
  },

  _setDealer(dealerNumber: string): void {
    const authStore = useAuthStore();
    const customer = this.participants.find(
      (participant) => participant.purpose === PARTICIPANT_TYPE.CUSTOMER
    );
    if (customer) {
      customer.attributes = {
        ...(customer.attributes ?? {}),
        [DEALER_ATTRIBUTE_KEY]: dealerNumber,
      };
    }

    const dealer = GenesysWebSocketService._extractDealerNumber(
      this.participants as never
    );
    authStore.setDealerNumber(dealer);
    if (dealer && BackendWebSocketService.isConnected) {
      void BackendWebSocketService.sendDealerInfo(dealer);
    }
  },

  _reset(): void {
    const authStore = useAuthStore();
    const messageStore = useMessageStore();
    messageStore.clearMessages();
    authStore.setDealerNumber(null);
    this.boundaryCounter = 0;
    this.lastAiResponseCount = 0;
    this.participants = this._buildParticipants('active');
  },

  // ---- helpers ------------------------------------------------------------

  _applyParticipants(participants: MockParticipant[]): void {
    const authStore = useAuthStore();
    const notification = {
      topicName: `v2.users.${authStore.userId}.conversations`,
      eventBody: {
        id: authStore.conversationId,
        participants,
      },
    };
    // Drives the real end-detection + boundary + maybePromptResume path.
    GenesysWebSocketService.handleMessage(notification as never);
  },

  _buildParticipants(
    phase: Phase,
    boundaryTime?: string,
    dealerNumber?: string
  ): MockParticipant[] {
    const authStore = useAuthStore();
    const agentId = authStore.userId || 'mock-agent-001';
    const attributes = dealerNumber
      ? { [DEALER_ATTRIBUTE_KEY]: dealerNumber }
      : undefined;

    if (phase === 'ended') {
      const t = boundaryTime || new Date().toISOString();
      return [
        { id: 'customer-1', purpose: PARTICIPANT_TYPE.CUSTOMER, endTime: t, attributes },
        { id: agentId, purpose: PARTICIPANT_TYPE.AGENT, endTime: t },
      ];
    }

    if (phase === 'resumed') {
      const t = boundaryTime || new Date().toISOString();
      return [
        { id: 'customer-1', purpose: PARTICIPANT_TYPE.CUSTOMER, endTime: t, attributes },
        { id: agentId, purpose: PARTICIPANT_TYPE.AGENT },
        { id: 'customer-2', purpose: PARTICIPANT_TYPE.CUSTOMER },
      ];
    }

    return [
      { id: 'customer-1', purpose: PARTICIPANT_TYPE.CUSTOMER, attributes },
      { id: agentId, purpose: PARTICIPANT_TYPE.AGENT },
    ];
  },

  _toMessage(entry: TranscriptEntry) {
    const inbound = entry.sender === 'customer';
    return {
      id: entry.id,
      text: entry.text,
      direction: inbound
        ? MESSAGE_DIRECTION.INBOUND
        : MESSAGE_DIRECTION.OUTBOUND,
      fromMe: !inbound,
      timestamp: new Date().toISOString(),
    };
  },

  // ---- FE -> harness ------------------------------------------------------

  _post(message: Record<string, unknown>): void {
    postToHarness(message);
  },

  _postState(): void {
    const authStore = useAuthStore();
    const messageStore = useMessageStore();

    // Emit any newly-arrived AI responses to the harness transcript.
    const responses = messageStore.aiResponses;
    if (responses.length > this.lastAiResponseCount) {
      for (let i = this.lastAiResponseCount; i < responses.length; i++) {
        const response = responses[i];
        this._post({
          type: 'pete:ai-response',
          payload: {
            translation: response.translation,
            answer_nl: response.agentAnswer,
            originalMessage: response.originalMessage,
            timestamp: response.timestamp,
          },
        });
      }
      this.lastAiResponseCount = responses.length;
    }

    this._post({
      type: 'pete:state',
      payload: {
        dealerNumber: authStore.dealerNumber,
        phase: messageStore.currentPhase,
        activityText: messageStore.currentActivityText,
        processing: messageStore.aiResponseProcessing.isProcessing,
        stage: messageStore.aiResponseProcessing.stage,
        attempt: messageStore.aiResponseProcessing.attempt,
        maxAttempts: messageStore.aiResponseProcessing.maxAttempts,
        conversationEnded: messageStore.conversationEnded,
        pendingResumeDecision: messageStore.pendingResumeDecision,
        pendingBoundaryId: messageStore.pendingBoundaryId,
        segmentMode: messageStore.segmentMode,
        previousSegmentSummary: messageStore.previousSegmentSummary,
        hasSummary: messageStore.hasSummary,
        summary: messageStore.currentSummary,
        recentActivity: messageStore.recentActivity.slice(0, 5),
      },
    });
  },
};
